import { useCallback, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Modal,
  Paper,
  Skeleton,
  SimpleGrid,
  Stack,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ButtonsWidget } from '@components/ButtonsWidget/ButtonsWidget';
import { UpcomingJobsWidget } from '@components/UpcomingJobsWidget/UpcomingJobsWidget';
import { FlipReviewsWidget } from '@components/FlipReviewsWidget/FlipReviewsWidget';
import { LeaderboardWidget } from '@components/LeaderboardWidget/LeaderboardWidget';
import { LevelsWidget } from '@components/LevelsWidget/LevelsWidget';
import { JobsCountersWidget } from '@components/JobsCountersWidget/JobsCountersWidget';
import {
  getLevel,
  getLeaderboard,
  getFlipReviews,
  markFlipReviewHelpful,
  markFlipReviewUnhelpful,
  getUpcomingJobs,
  getJobsCounters,
} from '@worker/services/workerDashboard/api';
import bisonBlack from '@assets/bison-black.png';
import { BASE_PATH } from '@utils/Environment';
import QueryKeys from '@constants/queryKeys';
import GetInNetwork from '@worker/components/GetInNetwork/GetInNetwork';
import { useWorkerStore } from '@worker/stores';
import AdBanner from '@components/GoogleAds/AdBanner';

const SERVER_URL = import.meta.env.VITE_SERVER_BASE_URL;
const UPCOMING_JOBS_MAX = 5;
const LEADERBOARD_MAX_HEIGHT = 710;
const FLIP_REVIEWS_HEIGHT = 380;

const GOOGLE_AD_SLOT_1 = '6711816885';
const GOOGLE_AD_SLOT_2 = '5656391442';

const OPEN_IN_NETWORK_BENEFITS = 'openInNetworkBenefits';

function WorkerDashboard() {
  const showGetInNetworkModal = useWorkerStore(
    (state) => state.showGetInNetworkModal
  );
  const setShowGetInNetworkModal = useWorkerStore(
    (state) => state.setShowGetInNetworkModal
  );
  const isInNetwork = useWorkerStore((state) => state.isInNetwork);

  const handleOpenInNetworkModal = useCallback(
    () => setShowGetInNetworkModal(true),
    [setShowGetInNetworkModal]
  );
  const handleCloseInNetworkModal = useCallback(
    () => setShowGetInNetworkModal(false),
    [setShowGetInNetworkModal]
  );

  // Open the Get In-Network modal when arriving via the benefits deep-link (e.g. after checkout)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.has(OPEN_IN_NETWORK_BENEFITS)) {
      setShowGetInNetworkModal(true);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete(OPEN_IN_NETWORK_BENEFITS);
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setShowGetInNetworkModal]);

  // TODO: tune staleTime for upcoming/leaderboard/level queries (Subtask 9).
  const { data: upcomingJobs = [] } = useQuery({
    queryKey: [QueryKeys.WorkerDashboard.UPCOMING_JOBS],
    queryFn: () => getUpcomingJobs(UPCOMING_JOBS_MAX),
  });

  const { data: flipReviews, isLoading: isLoadingFlipReviews } = useQuery({
    queryKey: [QueryKeys.WorkerDashboard.FLIP_REVIEWS],
    queryFn: getFlipReviews,
    staleTime: Infinity,
  });

  const { data: leaderboardData } = useQuery({
    queryKey: [QueryKeys.WorkerDashboard.LEADERBOARD],
    queryFn: getLeaderboard,
  });

  const { data: levelData } = useQuery({
    queryKey: [QueryKeys.WorkerDashboard.LEVEL],
    queryFn: getLevel,
  });

  const { data: jobsCountersData } = useQuery({
    queryKey: [QueryKeys.WorkerDashboard.JOBS_COUNTERS],
    queryFn: getJobsCounters,
  });

  const jobsCounters = jobsCountersData ? (
    <JobsCountersWidget
      myJobsCount={jobsCountersData.activeJobsByWorker}
      allJobsCount={jobsCountersData.totalActiveTHJobs}
      myJobsUrl={`${BASE_PATH}jobs/my-jobs/new`}
      allJobsUrl={`${SERVER_URL}/publicJobs/index`}
    />
  ) : (
    <SimpleGrid cols={2} spacing="md">
      <Skeleton h={130} />
      <Skeleton h={130} />
    </SimpleGrid>
  );

  const buttons = (
    <ButtonsWidget
      reviewsUrl={`${BASE_PATH}reviews`}
      surveysUrl={`${BASE_PATH}survey`}
      isInNetwork={isInNetwork()}
      onInNetworkClick={handleOpenInNetworkModal}
    />
  );
  const level = levelData ? (
    <LevelsWidget
      workerName={levelData.workerName}
      level={levelData.level}
      experiencePoints={levelData.experiencePoints}
      maxPoints={levelData.maxPoints}
      percentage={levelData.percentage}
      bisonImageUrl={bisonBlack}
      categories={levelData.categories}
    />
  ) : (
    <Skeleton h={200} />
  );
  const upcoming = (
    <UpcomingJobsWidget
      jobs={upcomingJobs}
      jobBaseUrl={`${SERVER_URL}/workerJobs/upcomingJob`}
      publicJobsUrl={`${SERVER_URL}/publicJobs/index`}
    />
  );
  const flip = (
    <Box pt={32}>
      {isLoadingFlipReviews ? (
        <Skeleton h={FLIP_REVIEWS_HEIGHT} />
      ) : (
        <FlipReviewsWidget
          reviews={flipReviews ?? []}
          onHelpful={markFlipReviewHelpful}
          onUnhelpful={markFlipReviewUnhelpful}
        />
      )}
    </Box>
  );
  const leaderboard = (
    <Paper
      shadow="var(--widget-shadow)"
      mah={LEADERBOARD_MAX_HEIGHT}
      style={{ overflow: 'auto' }}
    >
      {leaderboardData ? (
        <LeaderboardWidget {...leaderboardData} />
      ) : (
        <Skeleton h={LEADERBOARD_MAX_HEIGHT} />
      )}
    </Paper>
  );

  // Desktop (lg+): 3 columns, widgets stacked vertically inside each.
  // Mobile (<lg): single column. Order per client requirement:
  //   jobsCounters → buttons → level → upcoming → flip → leaderboard.
  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {isMobile ? (
          <Stack gap="md">
            {jobsCounters}
            {buttons}
            <AdBanner slot={GOOGLE_AD_SLOT_1} />
            {level}
            {upcoming}
            {flip}
            <AdBanner slot={GOOGLE_AD_SLOT_2} />
            {leaderboard}
          </Stack>
        ) : (
          <Grid gutter="xl">
            <Grid.Col span={4}>
              <Stack gap="md">
                {jobsCounters}
                {buttons}
                <AdBanner slot={GOOGLE_AD_SLOT_1} />
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              <Stack gap="md">
                {level}
                {flip}
                {upcoming}
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              <Stack gap="md">
                <AdBanner slot={GOOGLE_AD_SLOT_2} />
                {leaderboard}
              </Stack>
            </Grid.Col>
          </Grid>
        )}
      </Stack>
      <Modal
        opened={showGetInNetworkModal}
        onClose={handleCloseInNetworkModal}
        size={900}
        withCloseButton={false}
        radius="md"
        centered
        styles={{
          body: { padding: 0 },
          content: { overflow: 'hidden', padding: 0 },
        }}
      >
        <GetInNetwork
          onJoin={handleCloseInNetworkModal}
          onClose={handleCloseInNetworkModal}
        />
      </Modal>
    </Box>
  );
}

export default WorkerDashboard;
